import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Plane, ChevronDown, Search, Calendar, Users } from 'lucide-react';
import { DESTINATIONS, FLIGHT_SCHEDULES, ROUTE_PATHS, CONTACT_INFO } from '@/lib/index';
import Layout from '@/components/Layout';
import { springPresets } from '@/lib/motion';

// ─── Hero slides ─────────────────────────────────────────────────────────────
const SLIDES = [
  {
    image: '/images/57x42cm(Zanzibar)_ydknHi.jpg',
    country: 'ZANZIBAR · TANZANIA',
    title: '桑给巴尔',
    sub: '印度洋上的天堂之岛',
    code: 'ZNZ',
  },
  {
    image: '/images/zebras-running.jpg',
    country: 'SERENGETI · TANZANIA',
    title: '塞伦盖蒂',
    sub: '百万角马的壮阔迁徙',
    code: 'SEU',
  },
  {
    image: '/images/giraffe-kilimanjaro.jpg',
    country: 'KILIMANJARO · TANZANIA',
    title: '乞力马扎罗',
    sub: '非洲屋脊，云端俯瞰',
    code: 'JRO',
  },
  {
    image: '/images/cheetah.jpg',
    country: 'NGORONGORO · TANZANIA',
    title: '恩戈罗恩戈罗',
    sub: '世界最大完整火山口',
    code: 'ARK',
  },
  {
    image: '/images/57x42cm(Dreamliner).jpg',
    country: 'GUANGZHOU · CHINA',
    title: '梦想客机',
    sub: '广州直飞 · 每周三班出发',
    code: 'B787',
  },
];

// ─── Destination cards ────────────────────────────────────────────────────────
const DEST_CARDS = [
  { image: '/images/57x42cm(Zanzibar)_ydknHi.jpg',   name: '桑给巴尔', code: 'ZNZ', tag: '热门' },
  { image: '/images/zebras-running.jpg',              name: '塞伦盖蒂', code: 'SEU', tag: '野生动物' },
  { image: '/images/giraffe-kilimanjaro.jpg',         name: '乞力马扎罗', code: 'JRO', tag: '登山' },
  { image: '/images/57x42cm(Ngorongoro).jpg',         name: '恩戈罗恩戈罗', code: 'ARK', tag: '自然遗产' },
  { image: '/images/April 25x21.5cm (Zanzibar)_ydknHi.jpg', name: '塞舌尔', code: 'SEZ', tag: '🆕 新航点' },
  { image: '/images/africa-landscape.jpg',            name: '达累斯萨拉姆', code: 'DAR', tag: '枢纽城市' },
];

// ─── Experience strip ─────────────────────────────────────────────────────────
const EXP_STRIP = [
  { img: '/images/57x42cm(Experience).jpg',           label: '精选餐饮' },
  { img: '/images/25x21.5cm (Twiga Lounge)_gYUbNj.jpg', label: 'Twiga Lounge' },
  { img: '/images/25x21.5cm (Wine).jpg',              label: '机上佳酿' },
  { img: '/images/crew-smile.jpg',                    label: '专业空乘' },
  { img: '/images/57x42cm(Pilot).jpg',                label: '安全飞行' },
  { img: '/images/crew-model.jpg',                    label: '服务标准' },
];

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const total = SLIDES.length;
  const next = useCallback(() => setSlide(c => (c + 1) % total), [total]);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  return (
    <Layout>

      {/* ═══════════════════════════════════════════════
          HERO — Full viewport, cinematic
      ═══════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">

        {/* Parallax image stack */}
        <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <AnimatePresence mode="wait">
            <motion.img
              key={slide}
              src={SLIDES[slide].image}
              alt={SLIDES[slide].title}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: 'easeInOut' }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
        </motion.div>

        {/* Multi-layer gradient: richer depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-transparent via-40% to-secondary/85 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/50 via-transparent to-transparent pointer-events-none" />

        {/* Slide progress bar — top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 z-20">
          {SLIDES.map((_, i) => (
            <motion.div key={i}
              className="absolute top-0 h-full bg-accent"
              style={{ left: `${(i / total) * 100}%`, width: `${100 / total}%` }}
              initial={false}
              animate={{ scaleX: i === slide ? 1 : 0, originX: 0, opacity: i === slide ? 1 : 0.2 }}
              transition={{ duration: 6, ease: 'linear' }}
            />
          ))}
        </div>

        {/* Main content */}
        <motion.div style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col">

          {/* Spacer for header */}
          <div className="h-20 shrink-0" />

          {/* Centre-left headline */}
          <div className="flex-1 flex items-center">
            <div className="max-w-7xl mx-auto px-8 w-full">
              <AnimatePresence mode="wait">
                <motion.div key={slide}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={springPresets.smooth}
                  className="max-w-2xl">

                  {/* Country label */}
                  <motion.p
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
                    className="text-accent/90 text-[11px] font-black tracking-[.35em] uppercase mb-4 flex items-center gap-3">
                    <span className="inline-block w-8 h-px bg-accent/70" />
                    {SLIDES[slide].country}
                  </motion.p>

                  {/* Big title */}
                  <h1 className="text-[clamp(4rem,10vw,9rem)] font-black text-white leading-[0.9] tracking-tight mb-5 drop-shadow-2xl">
                    {SLIDES[slide].title}
                  </h1>

                  {/* Sub */}
                  <p className="text-white/70 text-lg md:text-xl font-light mb-8 tracking-wide">
                    {SLIDES[slide].sub}
                  </p>

                  {/* CTA buttons */}
                  <div className="flex flex-wrap gap-3 items-center">
                    <Link to={ROUTE_PATHS.BOOKING}
                      className="group flex items-center gap-2.5 px-7 py-4 bg-accent text-accent-foreground rounded-2xl font-bold text-sm hover:bg-accent/90 transition-all shadow-2xl shadow-accent/30 hover:shadow-accent/50 hover:gap-4">
                      立即订票
                      <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <Link to={ROUTE_PATHS.ROUTES}
                      className="flex items-center gap-2 px-7 py-4 rounded-2xl border border-white/25 text-white text-sm font-medium bg-white/8 backdrop-blur-md hover:bg-white/18 transition-all">
                      探索目的地
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom bar: slide indicators left + flight info right */}
          <div className="max-w-7xl mx-auto px-8 w-full pb-10">
            <div className="flex items-end justify-between gap-6">

              {/* Dot navigation */}
              <div className="flex items-center gap-3">
                {SLIDES.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)}
                    className={`transition-all duration-500 rounded-full ${
                      i === slide ? 'w-8 h-2 bg-accent' : 'w-2 h-2 bg-white/35 hover:bg-white/60'
                    }`} />
                ))}
              </div>

              {/* Compact flight info card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="hidden md:flex items-center gap-5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-6 py-4">
                <div className="flex items-center gap-3 text-white text-sm">
                  <Plane size={15} className="text-accent" />
                  <span className="text-white/60">广州 CAN</span>
                  <span className="text-white/30">→</span>
                  <span className="font-semibold">达累斯萨拉姆 DAR</span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="text-white/60 text-xs">每周一·三·五出发</div>
                <div className="w-px h-6 bg-white/20" />
                <div className="text-accent text-xs font-bold font-mono">B787-8</div>
              </motion.div>
            </div>

            {/* Compact search trigger */}
            <div className="mt-4">
              <button
                onClick={() => setSearchOpen(v => !v)}
                className="group w-full md:w-auto flex items-center gap-3 px-6 py-3.5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white text-sm hover:bg-white/18 transition-all">
                <Search size={15} className="text-accent" />
                <span className="text-white/70">搜索航班 — 选择日期 &amp; 目的地</span>
                <ChevronDown size={14} className={`ml-auto text-white/50 transition-transform ${searchOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Expandable search panel */}
              <AnimatePresence>
                {searchOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden mt-2">
                    <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl p-5">
                      <CompactSearch />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
          className="absolute bottom-24 right-8 flex flex-col items-center gap-1.5 text-white/30 z-10">
          <div className="text-[10px] tracking-widest uppercase writing-vertical">Scroll</div>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ChevronDown size={14} />
          </motion.div>
        </motion.div>
      </section>


      {/* ═══════════════════════════════════════════════
          FLIGHT SCHEDULE — Dark strip
      ═══════════════════════════════════════════════ */}
      <section className="bg-secondary text-white py-12">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-xl font-bold">广州出发 · 每周三班直飞东非</h2>
              <p className="text-white/40 text-sm mt-0.5">T2航站楼 · 波音 787-8 梦想客机</p>
            </div>
            <Link to={ROUTE_PATHS.BOOKING}
              className="group inline-flex items-center gap-2 text-accent text-sm font-semibold hover:gap-3 transition-all">
              查看完整时刻表 <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {FLIGHT_SCHEDULES.map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -3 }} className="bg-white/6 hover:bg-white/10 border border-white/8 rounded-2xl p-5 transition-colors cursor-default">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-accent font-bold text-xs tracking-wider">{f.flightNo}</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {f.days.map(d => (
                      <span key={d} className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-md text-white/60">{d}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-black font-mono tracking-tight">{f.departure}</div>
                    <div className="text-white/35 text-[11px] mt-1 font-mono">{f.fromCode}</div>
                  </div>
                  <div className="flex flex-col items-center gap-1 text-white/25">
                    <Plane size={13} className="text-accent/70" />
                    <div className="w-10 h-px bg-white/15" />
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-black font-mono tracking-tight">{f.arrival}</div>
                    <div className="text-white/35 text-[11px] mt-1 font-mono">{f.toCode}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════
          DESTINATIONS — Asymmetric masonry
      ═══════════════════════════════════════════════ */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-muted-foreground text-xs uppercase tracking-widest font-bold mb-2">Destinations</p>
              <h2 className="text-4xl font-black tracking-tight">探索东非之美</h2>
            </div>
            <Link to={ROUTE_PATHS.ROUTES}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
              全部目的地 <ArrowRight size={14} />
            </Link>
          </div>

          {/* Masonry — desktop 3 columns, mobile 2 columns */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2.5 auto-rows-[160px] md:auto-rows-[190px]">

            {/* Zanzibar — large hero */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-2 md:col-span-6 row-span-2 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[0].image} alt={DEST_CARDS[0].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <span className="inline-block text-[10px] font-bold bg-accent/80 text-accent-foreground px-2 py-0.5 rounded-full mb-2">{DEST_CARDS[0].tag}</span>
                <div className="font-mono text-white/50 text-xs mb-0.5">{DEST_CARDS[0].code}</div>
                <h3 className="text-white text-3xl font-black">{DEST_CARDS[0].name}</h3>
              </div>
            </Link>

            {/* Serengeti */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-1 md:col-span-4 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[1].image} alt={DEST_CARDS[1].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <div className="font-mono text-white/45 text-[10px] mb-0.5">{DEST_CARDS[1].code}</div>
                <h3 className="text-white text-lg font-black">{DEST_CARDS[1].name}</h3>
              </div>
            </Link>

            {/* Kilimanjaro */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-1 md:col-span-2 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[2].image} alt={DEST_CARDS[2].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute bottom-3 left-3">
                <h3 className="text-white text-sm font-black">{DEST_CARDS[2].name}</h3>
              </div>
            </Link>

            {/* Ngorongoro */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-1 md:col-span-3 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[3].image} alt={DEST_CARDS[3].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute bottom-3 left-3">
                <h3 className="text-white text-sm font-black">{DEST_CARDS[3].name}</h3>
              </div>
            </Link>

            {/* Seychelles — new */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-1 md:col-span-3 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[4].image} alt={DEST_CARDS[4].name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute top-3 right-3 text-[10px] font-bold bg-accent text-accent-foreground px-2 py-0.5 rounded-full">新航点</div>
              <div className="absolute bottom-3 left-3">
                <h3 className="text-white text-sm font-black">{DEST_CARDS[4].name}</h3>
              </div>
            </Link>

            {/* Dar es Salaam */}
            <Link to={ROUTE_PATHS.ROUTES}
              className="col-span-2 md:col-span-6 group relative overflow-hidden rounded-3xl">
              <img src={DEST_CARDS[5].image} alt={DEST_CARDS[5].name}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <div className="text-white/45 text-[10px] font-mono mb-0.5">{DEST_CARDS[5].code}</div>
                <h3 className="text-white text-lg font-black">{DEST_CARDS[5].name}</h3>
                <p className="text-white/55 text-xs">东非枢纽 · 直飞12h45m</p>
              </div>
            </Link>

          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════
          BRAND SPLIT — Aircraft photo + copy
      ═══════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-secondary">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[520px]">
          {/* Photo — full bleed */}
          <div className="relative h-72 lg:h-auto overflow-hidden order-2 lg:order-1">
            <img src="/images/57x42cm(Dreamliner).jpg" alt="梦想客机"
              className="w-full h-full object-cover" />
            {/* Right fade into dark bg */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-secondary hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-secondary/60 lg:hidden" />
          </div>

          {/* Copy */}
          <div className="text-white px-10 md:px-16 lg:px-20 py-16 lg:py-24 flex flex-col justify-center order-1 lg:order-2">
            <p className="text-accent text-[11px] uppercase tracking-[.3em] font-black mb-4">
              <span className="inline-block w-6 h-px bg-accent mr-3 align-middle" />
              The Wings of Kilimanjaro
            </p>
            <h2 className="text-4xl md:text-5xl font-black leading-[1.05] mb-5 tracking-tight">
              乞力马扎罗之翼<br />
              <span className="text-2xl md:text-3xl font-light text-white/55 tracking-normal">
                连接中国与东非的最短路径
              </span>
            </h2>
            <p className="text-white/55 leading-relaxed mb-8 text-sm max-w-md">
              坦桑尼亚国家航空公司，以波音787-8梦想客机执飞广州直飞航线，全程约12小时45分——
              比多数经停方案节省3至7小时，是目前中国至东非飞行时间最短的选择。
            </p>

            {/* 4 stats */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { v: '12h45m', l: '广州→达累斯萨拉姆', accent: true },
                { v: '3班/周', l: '每周直飞班次', accent: false },
                { v: '16架', l: '现代化机队规模', accent: false },
                { v: 'IOSA', l: '国际安全认证', accent: false },
              ].map(s => (
                <div key={s.l} className="bg-white/6 hover:bg-white/10 border border-white/8 rounded-2xl p-4 transition-colors">
                  <div className={`text-2xl font-black mb-0.5 ${s.accent ? 'text-accent' : 'text-white'}`}>{s.v}</div>
                  <div className="text-white/40 text-xs">{s.l}</div>
                </div>
              ))}
            </div>

            <Link to={ROUTE_PATHS.ABOUT}
              className="group inline-flex items-center gap-2 text-accent text-sm font-bold hover:gap-3 transition-all">
              了解我们的故事 <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════
          EXPERIENCE STRIP — Horizontal scroll
      ═══════════════════════════════════════════════ */}
      <section className="py-16 bg-muted overflow-hidden">
        <div className="max-w-7xl mx-auto px-8 mb-8">
          <p className="text-muted-foreground text-xs uppercase tracking-widest font-bold mb-2">Onboard</p>
          <h2 className="text-3xl font-black">卓越的飞行体验</h2>
        </div>

        <div className="flex gap-3 pl-8 overflow-x-auto pb-3 snap-x snap-mandatory scroll-pl-8"
          style={{ scrollbarWidth: 'none' }}>
          {EXP_STRIP.map((item) => (
            <motion.div key={item.label}
              whileHover={{ scale: 1.02 }} transition={springPresets.snappy}
              className="relative shrink-0 w-56 md:w-64 h-80 rounded-3xl overflow-hidden snap-start cursor-default">
              <img src={item.img} alt={item.label}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-white font-bold text-sm">{item.label}</div>
            </motion.div>
          ))}
          {/* Trailing spacer */}
          <div className="shrink-0 w-4" />
        </div>
      </section>


      {/* ═══════════════════════════════════════════════
          CARGO + TWIGA — Full-image 2-col
      ═══════════════════════════════════════════════ */}
      <section className="grid grid-cols-1 md:grid-cols-2">
        {[
          {
            to: ROUTE_PATHS.CARGO,
            img: '/images/57x42cm(Cargo Freighter)_ydknHi.jpg',
            eyebrow: 'Air Tanzania Cargo',
            title: '货运服务',
            desc: 'B767-300F · 最大载重54吨 · 广州直飞',
          },
          {
            to: ROUTE_PATHS.TWIGA_MILES,
            img: '/images/57x42cm(Mobile App).jpg',
            eyebrow: '常旅客计划',
            title: 'Twiga Miles',
            desc: '飞行积里程 · 兑换免费机票 · 专属特权',
          },
        ].map(card => (
          <Link key={card.title} to={card.to}
            className="group relative h-72 md:h-[420px] overflow-hidden">
            <img src={card.img} alt={card.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7">
              <p className="text-accent text-[11px] uppercase tracking-widest font-black mb-1.5">{card.eyebrow}</p>
              <h3 className="text-white text-3xl font-black mb-1">{card.title}</h3>
              <p className="text-white/55 text-sm mb-4">{card.desc}</p>
              <span className="inline-flex items-center gap-2 text-accent text-sm font-bold group-hover:gap-3 transition-all">
                了解详情 <ArrowRight size={13} />
              </span>
            </div>
          </Link>
        ))}
      </section>


      {/* ═══════════════════════════════════════════════
          CONTACT FOOTER STRIP
      ═══════════════════════════════════════════════ */}
      <section className="py-12 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="text-accent text-[11px] uppercase tracking-widest font-black mb-1">中国代表处</p>
              <h2 className="text-xl font-black mb-0.5">需要帮助？随时联系我们</h2>
              <p className="text-white/40 text-sm">广州 · 工作日 09:00–18:00 中文服务</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <a href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-2 px-5 py-3 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:bg-accent/90 transition shadow-lg shadow-accent/20">
                📞 {CONTACT_INFO.phone}
              </a>
              <a href={`mailto:${CONTACT_INFO.passengerEmail}`}
                className="px-5 py-3 bg-white/8 border border-white/15 text-white rounded-xl text-sm font-medium hover:bg-white/15 transition">
                ✉️ 发送邮件
              </a>
              <Link to={ROUTE_PATHS.CONTACT}
                className="px-5 py-3 bg-white/8 border border-white/15 text-white rounded-xl text-sm font-medium hover:bg-white/15 transition">
                在线留言
              </Link>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}

// ─── Compact inline search (no external component dep) ────────────────────────
function CompactSearch() {
  const DESTS = ['达累斯萨拉姆 (DAR)', '桑给巴尔 (ZNZ)', '约翰内斯堡 (JNB)', '内罗毕 (NBO)', '孟买 (BOM)', '迪拜 (DXB)', '塞舌尔 (SEZ)'];
  const [dest, setDest] = useState('');
  const [date, setDate] = useState('');
  const [pax, setPax] = useState(1);

  const handleSearch = () => {
    const params = new URLSearchParams({
      origin: 'CAN',
      destination: dest.split('(')[1]?.replace(')', '') || 'DAR',
      departureDate: date,
      numberOfPassengers: String(pax),
      cabin: 'Y',
    });
    window.open(`https://booking.airtanzania.co.tz/en/book/flights/oneway?${params}`, '_blank');
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
      <div>
        <label className="text-white/50 text-[11px] uppercase tracking-wider block mb-1.5">目的地</label>
        <select value={dest} onChange={e => setDest(e.target.value)}
          className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent appearance-none">
          <option value="" className="bg-secondary">选择目的地</option>
          {DESTS.map(d => <option key={d} value={d} className="bg-secondary">{d}</option>)}
        </select>
      </div>
      <div>
        <label className="text-white/50 text-[11px] uppercase tracking-wider block mb-1.5 flex items-center gap-1">
          <Calendar size={11} /> 出发日期
        </label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent" />
      </div>
      <div>
        <label className="text-white/50 text-[11px] uppercase tracking-wider block mb-1.5 flex items-center gap-1">
          <Users size={11} /> 人数
        </label>
        <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-3 py-2.5">
          <button onClick={() => setPax(p => Math.max(1, p - 1))} className="text-white/60 hover:text-white text-lg leading-none">−</button>
          <span className="text-white text-sm font-mono w-4 text-center">{pax}</span>
          <button onClick={() => setPax(p => Math.min(9, p + 1))} className="text-white/60 hover:text-white text-lg leading-none">+</button>
        </div>
      </div>
      <button onClick={handleSearch}
        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:bg-accent/90 transition whitespace-nowrap">
        <Search size={14} /> 搜索
      </button>
    </div>
  );
}
